import { useDrag } from '@use-gesture/react';
import { FunctionComponent, MouseEventHandler, useEffect, useRef } from 'react';
import { DraftConfiguration, Point, ViewMap } from '@/types';
import { scaleSize } from '@/model/configuration/scaleSize';
import { MultiSelectAdd } from '@/components/common/icons/MultiSelectAdd';
import { MultiSelectRemove } from '@/components/common/icons/MultiSelectRemove';
import { useDraftDispatch } from '@/components/draft/DraftContext';
import { setSelectedConfigurationAction } from '@/components/draft/draftState/actions/setSelectedConfigurationAction';
import { useMultiSelectContext } from '../MultiSelectContext';
import { useScaleFactor } from '../hooks/useScaleFactor';
import { getDeltaFromState } from '../utils/getDeltaFromState';
import { ConfigurationHandle } from './ConfigurationHandle';
import { ConfigurationMove } from './ConfigurationMove';
import './ConfigurationHandles.scss';

interface MultiSelectHandlesProps {
  configuration: DraftConfiguration;
  scale: number;
  viewMap: ViewMap;
  onMove: (offset: Point, first: boolean, last: boolean, dragging?: boolean) => void;
  snapLinesActive: boolean;
  onSetSnapLineActive: () => void;
}

export const MultiSelectHandles: FunctionComponent<MultiSelectHandlesProps> = ({
  configuration,
  scale,
  viewMap,
  onMove,
  snapLinesActive,
  onSetSnapLineActive,
}) => {
  const dispatch = useDraftDispatch();
  const [setRef, scaleFactor] = useScaleFactor(viewMap);
  const {
    isModeActive,
    configurationIds,
    addConfiguration,
    removeConfiguration,
    clearConfigurations,
    setTransformData,
    transformData,
  } = useMultiSelectContext();

  const configurationId = configuration.cid;
  const isInMultiSelect = configurationIds.includes(configurationId);

  const svgElementRef = useRef<SVGRectElement>(null);
  const moveElementRef = useRef<HTMLButtonElement>(null);
  const draggedRef = useRef(false);
  useDrag(
    (state) => {
      const [deltaX, deltaY] = state.delta;
      if (deltaX !== 0 || deltaY !== 0) {
        draggedRef.current = true;
      }

      if (!isInMultiSelect || state.touches > 1) {
        return;
      }

      const transformDelta = getDeltaFromState(state, svgElementRef);
      setTransformData([transformDelta, state.first, state.last, state.dragging]);
    },
    { target: moveElementRef }
  );

  useEffect(() => {
    // disable snapLines when using the configuration in multi-select
    if (isInMultiSelect && snapLinesActive) {
      onSetSnapLineActive();

      return () => onSetSnapLineActive();
    }
    // onSetSnapLineActive is not stable
    // snapLinesActive is only condition, should not trigger useEffect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInMultiSelect]);

  useEffect(() => {
    if (isInMultiSelect && transformData) {
      onMove(...transformData);
    }
    // onMove is not stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInMultiSelect, transformData]);

  const currentSize = scaleSize(configuration.size, scale);

  const handleSelect: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();

    if (draggedRef.current) {
      return (draggedRef.current = false);
    }

    if (!isModeActive && !(event.target as Element)?.closest('.configuration-handles__handle')) {
      clearConfigurations();
      return dispatch(setSelectedConfigurationAction(configuration));
    }

    if (isInMultiSelect) {
      return removeConfiguration(configurationId);
    }

    addConfiguration(configurationId);
  };

  return (
    <g ref={setRef} className="configuration-handles configuration-handles--multi-select">
      <rect
        ref={svgElementRef}
        width={currentSize.width}
        height={currentSize.height}
        className="configuration-handles__boundary configuration-handles__boundary--visible configuration-handles__boundary--multi-select"
      />
      <ConfigurationMove
        ref={moveElementRef}
        size={currentSize}
        active={isInMultiSelect}
        onClick={handleSelect}
      />
      <ConfigurationHandle
        x={currentSize.width / 2 - scaleFactor / 2}
        y={-scaleFactor}
        size={scaleFactor}
        Icon={isInMultiSelect ? MultiSelectRemove : MultiSelectAdd}
        onClick={handleSelect}
      />
    </g>
  );
};
